import React, { useCallback, useContext, useRef } from 'react';
import classnames from 'classnames';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import LazyContext from '@wix/da-react-context/pkg/LazyImageContext';
import { IconSize } from '../Icons/IconWrap';
import { useSliderOverflow } from './hooks/useSliderOverflow';
import { useSliderScrollToIndex } from './hooks/useSliderScrollToIndex';
import { useSliderScrollHandler } from './hooks/useSliderScrollHandler';
import DefaultPreviousButton from './lego/DefaultPreviousButton';
import DefaultNextButton from './lego/DefaultNextButton';
import { CommonArrowButtonProps } from './types';
import s from './Slider.scss';

export interface Props {
  children: React.ReactNode;
  className?: string;
  height?: number;
  slimSpacing?: boolean;
  noSpacing?: boolean;
  scrollToIndex?: number;
  smoothInitialScroll?: boolean;
  /** if value is 0..1 - assume it's percentage, if > 1 - assume it's pixels */
  scrollBy?: number;
  scrollSnap?: boolean;
  disableScroll?: boolean;
  disableArrows?: boolean;
  contentClassName?: string;

  permArrows?: boolean;
  arrowSize?: IconSize;
  compactArrows?: boolean;
  renderNextButton?: React.FC<CommonArrowButtonProps>;
  renderPrevButton?: React.FC<CommonArrowButtonProps>;
  dataHook?: string;

  onScrollBiData?: Record<string, any>;
  enableMobileSwipeLogging?: boolean;
  stableWidthButtons?: boolean;

  forceMobileArrows?: boolean;

  /** Optional ref to access the scrollable content container directly */
  contentRef?: React.RefObject<HTMLDivElement | null>;
}

const Slider: React.FC<Props> = ({
  smoothInitialScroll = true,
  scrollBy = 0.5,
  scrollSnap,
  className,
  height,
  children,
  disableScroll,
  slimSpacing,
  noSpacing,
  permArrows,
  arrowSize,
  compactArrows,
  dataHook,
  contentClassName,
  disableArrows,
  renderNextButton,
  renderPrevButton,
  scrollToIndex,
  onScrollBiData,
  enableMobileSwipeLogging,
  stableWidthButtons,
  forceMobileArrows,
  contentRef: externalContentRef,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isMobile = useContext(MobileContext);
  const itemCount = React.Children.count(children);
  const overflow = useSliderOverflow(contentRef, itemCount);

  // Set both internal and external refs, if provided
  const contentRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node;
      if (externalContentRef) {
        externalContentRef.current = node;
      }
    },
    [externalContentRef]
  );

  const handleScrollBtnClick = useSliderScrollHandler(
    contentRef,
    scrollBy,
    onScrollBiData,
    enableMobileSwipeLogging
  );
  useSliderScrollToIndex(
    contentRef,
    rootRef,
    scrollToIndex,
    smoothInitialScroll
  );

  const withControls = (!isMobile && !disableArrows) || forceMobileArrows;

  const handlePrevClick: React.MouseEventHandler<HTMLButtonElement> =
    useCallback(
      e => handleScrollBtnClick(e, 'previous'),
      [handleScrollBtnClick]
    );
  const handleNextClick: React.MouseEventHandler<HTMLButtonElement> =
    useCallback(e => handleScrollBtnClick(e, 'next'), [handleScrollBtnClick]);

  return (
    <LazyContext.Provider value={true}>
      <div
        className={classnames(
          s['root'],
          overflow && s[`overflow-${overflow}`],
          slimSpacing && s['slim-spacing'],
          noSpacing && s['no-spacing'],
          permArrows && s['perm-arrows'],
          compactArrows && s['compact-arrows'],
          stableWidthButtons && s['stable-width-buttons'],
          className
        )}
        style={{ height }}
        ref={rootRef}
        data-hook={dataHook}
      >
        <ErrorBoundary debugComponent="Slider">
          <div
            className={classnames(
              s['content'],
              disableScroll && s['no-scroll'],
              scrollSnap && s['scroll-snap'],
              contentClassName
            )}
            ref={contentRefCallback}
            draggable="false"
          >
            {children}
          </div>
          {withControls && (
            <>
              {renderPrevButton ? (
                (renderPrevButton({
                  onClick: handlePrevClick,
                  className: s['custom-prev-btn'],
                }) as React.ReactElement)
              ) : (
                <DefaultPreviousButton
                  arrowSize={arrowSize}
                  onClick={handlePrevClick}
                />
              )}
              {renderNextButton ? (
                (renderNextButton({
                  onClick: handleNextClick,
                  className: s['custom-next-btn'],
                }) as React.ReactElement)
              ) : (
                <DefaultNextButton
                  arrowSize={arrowSize}
                  onClick={handleNextClick}
                />
              )}
            </>
          )}
        </ErrorBoundary>
      </div>
    </LazyContext.Provider>
  );
};
Slider.displayName = 'Slider';

export default Slider;
